import React from 'react';
import { ArrowLeftIcon, DownloadIcon, PrinterIcon, CheckCircleIcon, AlertCircleIcon, BuildingIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { samplePlots } from '../data/sampleData';
export function CLOCertificatePage({
  plotId,
  navigateTo
}: {
  plotId: string | null;
  navigateTo: (page: string) => void;
}) {
  const {
    language,
    t
  } = useLanguage();
  const plot = samplePlots.find(p => p.id === plotId) || samplePlots[0];
  return <div className="space-y-6 max-w-5xl mx-auto pb-10" data-id="element-598">
      <div className="flex justify-between items-center" data-id="element-599">
        <button onClick={() => navigateTo('plotDetails')} className="flex items-center text-sm font-medium text-gray-500 hover:text-[#006A4E] transition-colors" data-id="element-600">
          <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-601" /> Back to Plot Details
        </button>
        <div className="flex space-x-3" data-id="element-602">
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm flex items-center shadow-sm" data-id="element-603">
            <PrinterIcon className="w-4 h-4 mr-2" data-id="element-604" /> Print
          </button>
          <button className="px-4 py-2 bg-[#006A4E] text-white rounded-lg font-medium hover:bg-[#004D3A] transition-colors text-sm flex items-center shadow-sm" data-id="element-605">
            <DownloadIcon className="w-4 h-4 mr-2" data-id="element-606" /> Download PDF
          </button>
        </div>
      </div>

      {/* The Certificate - Matching LandRecordDetailsPage Step 4 */}
      <div className="border-[12px] border-[#006A4E] p-1 bg-white relative mx-auto w-full shadow-xl" data-id="element-607">
        <div className="border-[4px] border-[#C9A84C] p-8 md:p-12 relative overflow-hidden" data-id="element-608">
          {/* Watermark */}
          <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none" data-id="element-609">
            <img src="https://cdn.magicpatterns.com/uploads/iWSbHRFjNdqxbSvrrHtxib/bdlogo.png" alt="Watermark" className="w-96 h-96 object-contain" data-id="element-610" />
          </div>

          <div className="relative z-10" data-id="element-611">
            <div className="flex justify-between items-start mb-8" data-id="element-612">
              <img src="https://cdn.magicpatterns.com/uploads/iWSbHRFjNdqxbSvrrHtxib/bdlogo.png" alt="Gov Logo" className="w-20 h-20 object-contain" data-id="element-613" />
              <div className="text-right" data-id="element-614">
                <p className="text-sm font-bold text-gray-600 uppercase tracking-widest mb-1" data-id="element-615">
                  Government of Bangladesh
                </p>
                <p className="text-xs text-gray-500" data-id="element-616">Ministry of Land</p>
              </div>
            </div>

            <div className="text-center mb-10" data-id="element-617">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#006A4E] mb-2" data-id="element-618">
                Certificate of Land Ownership
              </h2>
              <p className="text-gray-500 tracking-widest uppercase text-sm font-semibold" data-id="element-619">
                Digital Land Record System
              </p>
            </div>

            <div className="flex justify-between items-end border-b-2 border-gray-200 pb-4 mb-8" data-id="element-620">
              <div data-id="element-621">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-622">
                  Certificate No.
                </p>
                <p className="font-mono font-bold text-lg text-gray-900" data-id="element-623">
                  {plot.cloId || 'CLO-PENDING-2026'}
                </p>
              </div>
              <div className="text-right" data-id="element-624">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-625">
                  Date of Issue
                </p>
                <p className="font-bold text-gray-900" data-id="element-626">
                  {new Date().toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })}
                </p>
              </div>
            </div>

            <div className="space-y-8 mb-12" data-id="element-627">
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-628">
                This is to certify that{' '}
                <span className="font-bold text-gray-900 border-b border-gray-400 px-2" data-id="element-629">
                  {language === 'bn' ? (plot as any).ownerNameBn || plot.nameBn : (plot as any).ownerName || plot.name}
                </span>{' '}
                is the registered owner of the land parcel described below, as
                per the digital land records of the Government of Bangladesh.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-gray-50 p-6 rounded-lg border border-gray-200" data-id="element-630">
                <div data-id="element-631">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-632">
                    District
                  </p>
                  <p className="font-bold text-gray-900" data-id="element-633">
                    {language === 'bn' ? plot.districtBn : plot.district}
                  </p>
                </div>
                <div data-id="element-634">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-635">
                    Upazila
                  </p>
                  <p className="font-bold text-gray-900" data-id="element-636">
                    {language === 'bn' ? plot.upazilaBn : plot.upazila}
                  </p>
                </div>
                <div data-id="element-637">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-638">
                    Mouza
                  </p>
                  <p className="font-bold text-gray-900" data-id="element-639">
                    {language === 'bn' ? plot.mouzaBn : plot.mouza}
                  </p>
                </div>
                <div data-id="element-640">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-641">
                    Land Type
                  </p>
                  <p className="font-bold text-gray-900 capitalize" data-id="element-642">
                    {t(plot.type as any)}
                  </p>
                </div>
                <div data-id="element-643">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-644">
                    Khatian No.
                  </p>
                  <p className="font-bold text-gray-900" data-id="element-645">{plot.khatian}</p>
                </div>
                <div data-id="element-646">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-647">
                    Dag No.
                  </p>
                  <p className="font-bold text-gray-900" data-id="element-648">{plot.dag}</p>
                </div>
                <div className="col-span-2" data-id="element-649">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-650">
                    Total Area
                  </p>
                  <p className="font-bold text-gray-900" data-id="element-651">
                    {plot.area} Decimals
                  </p>
                </div>
              </div>

              {/* Sections: Tax, Case, Mortgage */}
              <div className="space-y-6" data-id="element-652">
                {/* LD TAX Clearance */}
                <div data-id="element-653">
                  <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3 border-b border-gray-200 pb-1" data-id="element-654">
                    LD TAX Payment Status
                  </h4>
                  {plot.taxHistory && plot.taxHistory.length > 0 ? <div className={`p-4 rounded-lg border ${plot.taxHistory[0].status === 'paid' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-id="element-655">
                      {plot.taxHistory[0].status === 'paid' ? <div className="flex items-center text-green-800 font-medium mb-2" data-id="element-656">
                          <CheckCircleIcon className="w-5 h-5 mr-2" data-id="element-657" />
                          All land development taxes are cleared
                        </div> : <div className="flex items-center text-red-800 font-medium mb-2" data-id="element-658">
                          <AlertCircleIcon className="w-5 h-5 mr-2" data-id="element-659" />
                          Tax payment pending for {plot.taxHistory[0].year}
                        </div>}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3" data-id="element-660">
                        <div data-id="element-661">
                          <span className="text-gray-500 block" data-id="element-662">
                            Latest Year
                          </span>
                          <span className="font-semibold text-gray-900" data-id="element-663">
                            {plot.taxHistory[0].year}
                          </span>
                        </div>
                        <div data-id="element-664">
                          <span className="text-gray-500 block" data-id="element-665">Amount</span>
                          <span className="font-semibold text-gray-900" data-id="element-666">
                            {plot.taxHistory[0].amount}
                          </span>
                        </div>
                        <div data-id="element-667">
                          <span className="text-gray-500 block" data-id="element-668">Status</span>
                          <span className={`font-semibold ${plot.taxHistory[0].status === 'paid' ? 'text-green-700' : 'text-red-700'}`} data-id="element-669">
                            {plot.taxHistory[0].status.toUpperCase()}
                          </span>
                        </div>
                        <div data-id="element-670">
                          <span className="text-gray-500 block" data-id="element-671">
                            Dhakhila No.
                          </span>
                          <span className="font-mono font-semibold text-gray-900" data-id="element-672">
                            {plot.taxHistory[0].dhakhilaNo || '-'}
                          </span>
                        </div>
                      </div>
                    </div> : <p className="text-sm text-gray-500 italic" data-id="element-673">
                      No tax records available.
                    </p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-674">
                  {/* Litigation Status */}
                  <div data-id="element-675">
                    <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3 border-b border-gray-200 pb-1" data-id="element-676">
                      Litigation Status
                    </h4>
                    {plot.litigations && plot.litigations.length > 0 ? <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg" data-id="element-677">
                        <div className="flex items-start text-amber-800" data-id="element-678">
                          <AlertCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-679" />
                          <div data-id="element-680">
                            <p className="font-bold mb-1" data-id="element-681">
                              Active Litigation Found
                            </p>
                            <p className="text-sm" data-id="element-682">
                              Case No:{' '}
                              <span className="font-mono" data-id="element-683">
                                {plot.litigations[0].caseNo}
                              </span>
                            </p>
                            <p className="text-sm" data-id="element-684">
                              Court:{' '}
                              {language === 'bn' ? plot.litigations[0].courtBn : plot.litigations[0].court}
                            </p>
                          </div>
                        </div>
                      </div> : <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg flex items-center text-gray-700" data-id="element-685">
                        <CheckCircleIcon className="w-5 h-5 mr-2 text-green-500" data-id="element-686" />
                        <span className="font-medium text-sm" data-id="element-687">
                          No active litigations or disputes.
                        </span>
                      </div>}
                  </div>

                  {/* Mortgage Status */}
                  <div data-id="element-688">
                    <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3 border-b border-gray-200 pb-1" data-id="element-689">
                      Mortgage Information
                    </h4>
                    {plot.mortgages && plot.mortgages.some((m: any) => m.status === 'active') ? <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg" data-id="element-690">
                        <div className="flex items-start text-blue-800" data-id="element-691">
                          <BuildingIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-692" />
                          <div data-id="element-693">
                            <p className="font-bold mb-1" data-id="element-694">
                              Active Mortgage Found
                            </p>
                            <p className="text-sm" data-id="element-695">
                              Bank:{' '}
                              {language === 'bn' ? plot.mortgages.find((m: any) => m.status === 'active').bankBn : plot.mortgages.find((m: any) => m.status === 'active').bank}
                            </p>
                            <p className="text-sm" data-id="element-696">
                              Amount:{' '}
                              {plot.mortgages.find((m: any) => m.status === 'active').amount}
                            </p>
                          </div>
                        </div>
                      </div> : <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg flex items-center text-gray-700" data-id="element-697">
                        <CheckCircleIcon className="w-5 h-5 mr-2 text-green-500" data-id="element-698" />
                        <span className="font-medium text-sm" data-id="element-699">
                          No active mortgages.
                        </span>
                      </div>}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-end mt-16" data-id="element-700">
              <div className="w-28 h-28 bg-white border-2 border-gray-200 p-1 flex items-center justify-center" data-id="element-701">
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://land.gov.bd/verify/${plot.cloId || 'CLO-PENDING'}`} alt="QR Code" className="w-full h-full object-contain opacity-90" data-id="element-702" />
              </div>
              <div className="text-center" data-id="element-703">
                <div className="w-48 border-b border-gray-400 mb-2" data-id="element-704"></div>
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest" data-id="element-705">
                  Digital Signature
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-706">Ministry of Land</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}